import { supabase } from "./supabase";
import SubstackAPI, { SubstackPost, SyncStatus, createSubstackConfig, substackUtils } from "./substackApi";
import { BlogPost } from "../types/blog";

// Sync configuration and state management
export interface SubstackSyncConfig {
  publicationUrl: string;
  autoSyncEnabled: boolean;
  lastSyncAt?: string;
  syncMethod: 'email' | 'api' | 'manual';
}

export interface SubstackSyncResult {
  success: boolean;
  syncedPosts: number;
  failedPosts: number;
  errors: string[];
  details: SyncStatus[];
}

class SubstackSyncManager {
  private api: SubstackAPI | null = null;
  private config: SubstackSyncConfig | null = null;

  // Initialize with configuration
  async initialize(config: SubstackSyncConfig): Promise<void> {
    this.config = config;
    const substackConfig = createSubstackConfig(config.publicationUrl);
    this.api = new SubstackAPI(substackConfig);
  }

  // Get sync configuration from database
  async getSyncConfig(): Promise<SubstackSyncConfig | null> {
    try {
      const { data, error } = await supabase
        .from('substack_config')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching sync config:', error);
        return null;
      }

      if (!data) return null;

      // Map snake_case DB columns to camelCase TS properties
      return {
        publicationUrl: data.publication_url,
        autoSyncEnabled: data.auto_sync_enabled,
        lastSyncAt: data.last_sync_at,
        syncMethod: data.sync_method
      };
    } catch (error) {
      console.error('Error getting sync config:', error);
      return null;
    }
  }

  // Save sync configuration to database
  async saveSyncConfig(config: SubstackSyncConfig): Promise<boolean> {
    try {
      // First check if a config exists
      const { data: existingConfig } = await supabase
        .from('substack_config')
        .select('id')
        .single();

      // Map camelCase TS properties to snake_case DB columns
      const dbConfig = {
        publication_url: config.publicationUrl,
        auto_sync_enabled: config.autoSyncEnabled,
        sync_method: config.syncMethod,
        last_sync_at: config.lastSyncAt,
        updated_at: new Date().toISOString()
      };

      let error;
      if (existingConfig?.id) {
        // Update existing config
        const result = await supabase
          .from('substack_config')
          .update(dbConfig)
          .eq('id', existingConfig.id);
        error = result.error;
      } else {
        // Insert new config
        const result = await supabase
          .from('substack_config')
          .insert(dbConfig);
        error = result.error;
      }

      if (error) {
        console.error('Error saving sync config:', error);
        return false;
      }

      this.config = config;
      await this.initialize(config);
      return true;
    } catch (error) {
      console.error('Error saving sync config:', error);
      return false;
    }
  }

  // Get sync status for all posts
  async getSyncStatuses(): Promise<SyncStatus[]> {
    try {
      const { data, error } = await supabase
        .from('substack_sync_status')
        .select('*')
        .order('synced_at', { ascending: false });

      if (error) {
        console.error('Error fetching sync statuses:', error);
        return [];
      }

      // Map snake_case DB columns to camelCase TS properties
      return (data || []).map(row => ({
        postId: row.post_id,
        substackUrl: row.substack_url,
        syncedAt: row.synced_at,
        status: row.status,
        error: row.error
      }));
    } catch (error) {
      console.error('Error getting sync statuses:', error);
      return [];
    }
  }

  // Update sync status for a post
  async updateSyncStatus(status: SyncStatus): Promise<void> {
    try {
      // Map camelCase TS properties to snake_case DB columns
      const dbStatus = {
        post_id: status.postId,
        substack_url: status.substackUrl,
        synced_at: status.syncedAt,
        status: status.status,
        error: status.error,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('substack_sync_status')
        .upsert(dbStatus, { onConflict: 'post_id' });

      if (error) {
        console.error('Error updating sync status:', error);
      }
    } catch (error) {
      console.error('Error updating sync status:', error);
    }
  }

  // Sync a single post to Substack
  async syncPost(blogPost: BlogPost): Promise<SyncStatus> {
    if (!this.api || !this.config) {
      throw new Error('Substack sync not initialized');
    }

    const syncStatus: SyncStatus = {
      postId: blogPost.id,
      syncedAt: new Date().toISOString(),
      status: 'pending'
    };

    try {
      // Convert blog post to Substack format
      const substackPost = substackUtils.convertBlogPostToSubstack(blogPost);
      
      // Prepare content for Substack
      substackPost.content = substackUtils.prepareContentForSubstack(substackPost.content);

      let result;
      
      // Choose sync method based on configuration
      switch (this.config.syncMethod) {
        case 'email':
          result = await this.api.publishViaEmail(substackPost);
          break;
        case 'api':
          result = await this.api.publishPost(substackPost);
          break;
        default:
          result = { 
            success: false, 
            error: 'Manual sync method selected - use manual sync button' 
          };
      }

      if (result.success) {
        syncStatus.status = 'synced';
        syncStatus.substackUrl = (result as any).postUrl;
      } else {
        syncStatus.status = 'failed';
        syncStatus.error = result.error;
      }

    } catch (error) {
      syncStatus.status = 'failed';
      syncStatus.error = error instanceof Error ? error.message : 'Unknown error';
    }

    // Save sync status to database
    await this.updateSyncStatus(syncStatus);
    
    return syncStatus;
  }

  // Sync all posts to Substack
  async syncAllPosts(posts: BlogPost[]): Promise<SubstackSyncResult> {
    const result: SubstackSyncResult = {
      success: true,
      syncedPosts: 0,
      failedPosts: 0,
      errors: [],
      details: []
    };

    if (!this.api || !this.config) {
      result.success = false;
      result.errors.push('Substack sync not configured');
      return result;
    }

    // Get existing sync statuses to avoid re-syncing
    const existingSyncStatuses = await this.getSyncStatuses();
    const syncedPostIds = new Set(
      existingSyncStatuses
        .filter(status => status.status === 'synced')
        .map(status => status.postId)
    );

    // Filter posts to sync (exclude drafts and already synced posts)
    const postsToSync = posts.filter(post => 
      !post.isDraft && !syncedPostIds.has(post.id)
    );

    console.log(`Syncing ${postsToSync.length} posts to Substack...`);

    // Sync posts with delay to avoid rate limiting
    for (const post of postsToSync) {
      try {
        const syncStatus = await this.syncPost(post);
        result.details.push(syncStatus);

        if (syncStatus.status === 'synced') {
          result.syncedPosts++;
        } else {
          result.failedPosts++;
          if (syncStatus.error) {
            result.errors.push(`${post.title}: ${syncStatus.error}`);
          }
        }

        // Add delay between syncs to be respectful
        await new Promise(resolve => setTimeout(resolve, 2000));
        
      } catch (error) {
        result.failedPosts++;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        result.errors.push(`${post.title}: ${errorMessage}`);
      }
    }

    // Update last sync time
    if (this.config) {
      this.config.lastSyncAt = new Date().toISOString();
      await this.saveSyncConfig(this.config);
    }

    result.success = result.errors.length === 0;
    return result;
  }

  // Auto-sync on post publish (called when a post is published)
  async autoSyncOnPublish(blogPost: BlogPost): Promise<void> {
    if (!this.config?.autoSyncEnabled) {
      return;
    }

    try {
      console.log(`Auto-syncing post to Substack: ${blogPost.title}`);
      await this.syncPost(blogPost);
    } catch (error) {
      console.error('Auto-sync failed:', error);
      // Don't throw error to avoid blocking the publish process
    }
  }

  // Check if Substack configuration is valid
  isConfigured(): boolean {
    return !!(this.config && this.api);
  }

  // Generate sync instructions for manual method
  generateManualSyncInstructions(blogPost: BlogPost): string {
    const substackPost = substackUtils.convertBlogPostToSubstack(blogPost);
    
    return `
Manual Sync Instructions for: ${blogPost.title}

1. Go to your Substack dashboard: https://arvindkc.substack.com/publish
2. Create a new post
3. Copy and paste the following:

Title: ${substackPost.title}
${substackPost.subtitle ? `Subtitle: ${substackPost.subtitle}` : ''}

Content:
${substackPost.content}

${substackPost.coverImage ? `Cover Image: ${substackPost.coverImage}` : ''}

4. Review and publish the post
5. Copy the published URL back to mark as synced
    `.trim();
  }
}

// Create singleton instance
export const substackSync = new SubstackSyncManager();

// Utility function to check if post needs syncing
export const needsSync = (post: BlogPost, syncStatuses: SyncStatus[]): boolean => {
  if (post.isDraft) return false;
  
  const syncStatus = syncStatuses.find(s => s.postId === post.id);
  return !syncStatus || syncStatus.status !== 'synced';
};

// Utility function to format sync status for display
export const formatSyncStatus = (status: SyncStatus): string => {
  switch (status.status) {
    case 'synced':
      return '✅ Synced';
    case 'pending':
      return '⏳ Pending';
    case 'failed':
      return '❌ Failed';
    default:
      return '❓ Unknown';
  }
};